import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useRolodexEntries } from 'features/rolodex'
import first from 'lodash/first'
import isEqual from 'lodash/isEqual'
import { useMemo } from 'react'
import {
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoComponentDataset,
  GeckoEntry,
  GeckoInspection,
  GeckoUnit,
  RolodexEntryType,
} from 'types'

export interface LibraAssetModelData {
  isLoading: boolean
  unit: GeckoUnit | undefined
  siteName: string
  assetModel: GeckoAssetModel | undefined
  /** The unit's component data frames (CDFs). */
  componentDataFrames: ReadonlyArray<GeckoComponentDataFrame>
  /** The unit's component datasets (CDS). */
  componentDatasets: ReadonlyArray<GeckoComponentDataset>
  components: ReadonlyArray<GeckoComponent>
  inspections: ReadonlyArray<GeckoInspection>
  /**
   * Raw Rolodex inspection entries filtered from the components-and-inspections
   * query. Used by callers that need `entry.updated_at` or other envelope
   * fields the parsed `GeckoInspection` doesn't carry — see
   * `selectComponentInspection.ts` (`pickMostRecentInspection`) for the
   * canonical ranking implementation used by the tab layer.
   */
  inspectionEntries: ReadonlyArray<GeckoEntry>
  /**
   * Granular loading flag for ONLY the components+inspections query.
   * Distinct from `isLoading` (which is the aggregate across unit, sites,
   * and the components+inspections bundle). Use this when downstream
   * behaviour only needs the components/inspections data and a slow site
   * lookup shouldn't block.
   */
  isLoadingComponentsAndInspections: boolean
  /**
   * SWR error from the components+inspections query, if any. Other
   * sub-queries' errors are NOT surfaced here.
   */
  componentsAndInspectionsError: Error | undefined
  /**
   * Refetch the components+inspections query. Forwards `forceRefresh`
   * from the underlying `useRolodexEntries`.
   */
  forceRefresh: () => Promise<void>
}

/**
 * Parameterized inner hook: fetches the asset model data (unit, site, asset
 * model, components, inspections, component datasets) for an explicit
 * `unitId`. Used by:
 * - `useLibraAssetModelData()` (the thin wrapper below) inside a mounted
 *   ticket context.
 * - Ticket creation flows that need to read a unit's data
 *   *before* a ticket exists — those callers pass the unit id directly.
 */
export function useLibraAssetModelDataForUnit(
  unitId: string | undefined
): LibraAssetModelData {
  const unitQueryBody = useMemo(
    () =>
      unitId
        ? {
            tags: { unit: unitId },
            type: [RolodexEntryType.UNIT],
          }
        : undefined,
    [unitId]
  )
  const { units, isLoading: isLoadingUnit } = useRolodexEntries(unitQueryBody, {
    swrOptions: { revalidateOnFocus: false, compare: isEqual },
  })

  const entriesQueryBody = useMemo(
    () =>
      unitId
        ? {
            tags: { unit: unitId },
            type: [
              RolodexEntryType.COMPONENT,
              RolodexEntryType.INSPECTION,
              RolodexEntryType.ASSET_MODEL,
              RolodexEntryType.COMPONENT_DATA_FRAME,
              RolodexEntryType.COMPONENT_DATASET,
            ],
          }
        : undefined,
    [unitId]
  )
  const {
    entries: bundleEntries,
    components,
    inspections,
    assetModels,
    componentDataFrames,
    componentDatasets,
    isLoading,
    error: bundleError,
    forceRefresh,
  } = useRolodexEntries(entriesQueryBody, {
    swrOptions: { revalidateOnFocus: false, compare: isEqual },
  })

  const sitesQueryBody = useMemo(
    () =>
      unitId
        ? {
            ids: units
              .map((unit) => unit.tags.site)
              .filter((site): site is string => site !== undefined),
            type: [RolodexEntryType.SITE],
          }
        : undefined,
    [unitId, units]
  )
  const { sites, isLoading: isLoadingSites } = useRolodexEntries(
    sitesQueryBody,
    { swrOptions: { revalidateOnFocus: false, compare: isEqual } }
  )

  const inspectionEntries = useMemo(
    () => bundleEntries.filter((e) => e.type === RolodexEntryType.INSPECTION),
    [bundleEntries]
  )

  return useMemo(() => {
    const unit = first(units)
    const site = first(sites)
    const assetModel = first(assetModels)
    return {
      isLoading: isLoadingUnit || isLoading || isLoadingSites,
      unit,
      assetModel,
      siteName: site?.data.name ?? '',
      components,
      inspections,
      componentDataFrames,
      componentDatasets,
      inspectionEntries,
      isLoadingComponentsAndInspections: isLoading,
      componentsAndInspectionsError: bundleError,
      forceRefresh,
    }
  }, [
    assetModels,
    bundleError,
    componentDataFrames,
    componentDatasets,
    components,
    forceRefresh,
    inspectionEntries,
    inspections,
    isLoading,
    isLoadingSites,
    isLoadingUnit,
    sites,
    units,
  ])
}

/**
 * Hook that fetches asset model data for the ticket currently in scope.
 * Thin wrapper around `useLibraAssetModelDataForUnit` that pulls `unitId`
 * from the surrounding `TicketAccessContext` — works only inside a mounted
 * ticket.
 */
export function useLibraAssetModelData(): LibraAssetModelData {
  const { ticketTags } = useTicketAccess()
  return useLibraAssetModelDataForUnit(ticketTags.unit ?? undefined)
}
